import { config } from '@iheartjane/config';
import type { LatLong } from '@iheartjane/geolocation';
import type { StoreType } from '@iheartjane/types';
import { useQuery } from '@tanstack/react-query';

import { api } from '@jane/shared/data-access';
import type {
  StoreFulfillmentTypes,
  UserPreferences,
} from '@jane/shared/types';
import { encodeQuery } from '@jane/shared/util';

interface ApiParams {
  distance?: number;
  fulfillment_type?: StoreFulfillmentTypes;
  lat?: number;
  long?: number;
  open_now?: boolean;
  store_type?: StoreType;
  zipcode?: string;
}

interface ApiResponse {
  store_ids: number[];
}

export const fetchUserPreferredStoreIds = async ({
  coordinates,
  zipcode,
  userPreferences = {},
}: {
  coordinates?: LatLong;
  userPreferences: UserPreferences;
  zipcode?: string;
}): Promise<ApiResponse['store_ids']> => {
  const params: ApiParams = {
    ...coordinates,
    zipcode,
    distance: userPreferences.storeSearchRadius,
    fulfillment_type: userPreferences.storeFulfillmentType,
    store_type: userPreferences.storeType,
  };

  if (userPreferences.storeAvailability === 'openNow') {
    params.open_now = true;
  }

  const url = encodeQuery(
    `${config.apiPath}/stores/ids_by_shopping_preferences`,
    params
  );

  const res: ApiResponse = await api.get(url);

  return res.store_ids;
};

export const useUserPreferredStoreIds = ({
  coordinates,
  zipcode,
  userPreferences = {},
}: {
  coordinates?: LatLong;
  userPreferences?: UserPreferences;
  zipcode?: string;
}) =>
  useQuery({
    enabled: !!coordinates?.lat || !!coordinates?.long || !!zipcode,
    queryFn: () =>
      fetchUserPreferredStoreIds({ coordinates, zipcode, userPreferences }),
    queryKey: ['userPreferredStores', coordinates, userPreferences, zipcode],
  });
